<template>
  <div class="w-full">
    <!-- Header with Search and Add Button -->
    <div class="flex justify-between items-center mb-6">
      <div class="relative w-1/3">
        <input
          type="text"
          v-model="searchTerm"
          placeholder="Search..."
          class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @input="handleSearch"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
      <button
        @click="openEntryModal()"
        class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        New Entry
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <svg class="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline"> {{ error.message }}</span>
      <button
        @click="fetchEntries"
        class="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      >
        Try Again
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredEntries.length === 0" class="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-8 rounded-md text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No entries found</h3>
      <p class="mt-1 text-sm text-gray-500">
        {{ searchTerm ? 'No entries match your search criteria.' : 'Start by adding a new entry.' }}
      </p>
      <div class="mt-6">
        <button
          @click="openEntryModal()"
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
          </svg>
          New Entry
        </button>
      </div>
    </div>

    <!-- Data Table -->
    <div v-else class="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Topic
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Subtopic
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="entry in filteredEntries" :key="entry.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ entry.topic }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900">{{ entry.subtopic }}</div>
            </td>
            <td class="px-6 py-4">
              <div class="text-sm text-gray-900 max-w-md">
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(point, index) in parseBulletpoints(entry.description)" :key="index">
                    {{ point }}
                  </li>
                </ul>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
              <button
                @click="openEntryModal(entry)"
                class="text-indigo-600 hover:text-indigo-900 mr-4"
                title="Edit"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
              <button
                @click="confirmDelete(entry)"
                class="text-red-600 hover:text-red-900"
                title="Delete"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Entry Modal -->
    <div v-if="showEntryModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeEntryModal"></div>
      
      <!-- Modal Content -->
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl z-10 relative">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">
            {{ currentEntry.id ? 'Edit Entry' : 'New Entry' }}
          </h3>
        </div>
        
        <form @submit.prevent="saveEntry" class="px-6 py-4">
          <!-- Topic Field -->
          <div class="mb-4">
            <label for="topic" class="block text-sm font-medium text-gray-700 mb-1">Topic</label>
            <input
              id="topic"
              v-model="currentEntry.topic"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <!-- Subtopic Field -->
          <div class="mb-4">
            <label for="subtopic" class="block text-sm font-medium text-gray-700 mb-1">Subtopic</label>
            <input
              id="subtopic"
              v-model="currentEntry.subtopic"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <!-- Description Field (Bulletpoints) -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Description (Bulletpoints)</label>
            <div class="space-y-2">
              <div v-for="(point, index) in bulletpoints" :key="index" class="flex items-center">
                <span class="mr-2 text-gray-500">-</span>
                <input
                  v-model="bulletpoints[index]"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Bulletpoint"
                />
                <button
                  type="button"
                  @click="removeBulletpoint(index)"
                  class="ml-2 text-red-600 hover:text-red-900"
                >
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
              <button
                type="button"
                @click="addBulletpoint"
                class="mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                Add Bulletpoint
              </button>
            </div>
          </div>
          
          <!-- Error Message -->
          <div v-if="formError" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <strong class="font-bold">Error!</strong>
            <span class="block sm:inline"> {{ formError }}</span>
          </div>
          
          <!-- Form Actions -->
          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="closeEntryModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="formSubmitting"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="formSubmitting">Saving...</span>
              <span v-else>Save</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeDeleteModal"></div>
      
      <!-- Modal Content -->
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md z-10 relative">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">Delete Entry</h3>
        </div>
        
        <div class="px-6 py-4">
          <p class="text-gray-700">
            Are you sure you want to delete this entry?
          </p>
          <p class="mt-2 font-medium text-gray-900">
            {{ entryToDelete?.topic }} - {{ entryToDelete?.subtopic }}
          </p>
        </div>
        
        <div class="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            @click="deleteEntry"
            :disabled="deleteSubmitting"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="deleteSubmitting">Deleting...</span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { KnowledgebaseService, KnowledgebaseEntry } from '@/services/knowledgebaseService';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'ManageKnowledgebase',
  emits: ['navigate'],
  setup() {
    // Create a new instance of the KnowledgebaseService
    const knowledgebaseService = new KnowledgebaseService();
    
    // State
    const entries = ref<KnowledgebaseEntry[]>([]);
    const loading = ref(false);
    const error = ref<Error | null>(null);
    const searchTerm = ref('');
    
    // Modal state
    const showEntryModal = ref(false);
    const currentEntry = ref<KnowledgebaseEntry>({
      topic: '',
      subtopic: '',
      description: ''
    });
    const bulletpoints = ref<string[]>([]);
    const formError = ref<string | null>(null);
    const formSubmitting = ref(false);
    
    // Delete modal state
    const showDeleteModal = ref(false);
    const entryToDelete = ref<KnowledgebaseEntry | null>(null);
    const deleteSubmitting = ref(false);
    
    // Computed
    const filteredEntries = computed(() => {
      if (!searchTerm.value) return entries.value;
      
      const term = searchTerm.value.toLowerCase();
      return entries.value.filter(entry => 
        entry.topic.toLowerCase().includes(term) ||
        entry.subtopic.toLowerCase().includes(term) ||
        entry.description.toLowerCase().includes(term)
      );
    });
    
    // Methods
    const fetchEntries = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        entries.value = await knowledgebaseService.getEntries();
      } catch (err) {
        logger.error('Error fetching knowledgebase entries', LogCategory.DATABASE, { error: err });
        error.value = err instanceof Error ? err : new Error('Failed to fetch entries');
      } finally {
        loading.value = false;
      }
    };
    
    const handleSearch = () => {
      // This function is called on input event
      // The filtering is handled by the computed property
    };
    
    const openEntryModal = (entry?: KnowledgebaseEntry) => {
      formError.value = null;
      
      if (entry) {
        // Edit existing entry
        currentEntry.value = { ...entry };
        bulletpoints.value = knowledgebaseService.parseBulletpoints(entry.description);
      } else {
        // Create new entry
        currentEntry.value = {
          topic: '',
          subtopic: '',
          description: ''
        };
        bulletpoints.value = [''];
      }
      
      showEntryModal.value = true;
    };
    
    const closeEntryModal = () => {
      showEntryModal.value = false;
      formError.value = null;
    };
    
    const addBulletpoint = () => {
      bulletpoints.value.push('');
    };
    
    const removeBulletpoint = (index: number) => {
      bulletpoints.value.splice(index, 1);
      
      // Always keep at least one bulletpoint
      if (bulletpoints.value.length === 0) {
        bulletpoints.value.push('');
      }
    };
    
    const saveEntry = async () => {
      formSubmitting.value = true;
      formError.value = null;
      
      try {
        // Format bulletpoints into description
        currentEntry.value.description = knowledgebaseService.formatBulletpoints(bulletpoints.value);
        
        if (currentEntry.value.id) {
          // Update existing entry
          await knowledgebaseService.updateEntry(currentEntry.value.id, currentEntry.value);
          logger.info('Updated knowledgebase entry', LogCategory.DATABASE, { 
            id: currentEntry.value.id,
            topic: currentEntry.value.topic
          });
        } else {
          // Create new entry
          await knowledgebaseService.createEntry(currentEntry.value);
          logger.info('Created new knowledgebase entry', LogCategory.DATABASE, { 
            topic: currentEntry.value.topic
          });
        }
        
        // Refresh entries
        await fetchEntries();
        
        // Close modal
        closeEntryModal();
      } catch (err) {
        logger.error('Error saving knowledgebase entry', LogCategory.DATABASE, { error: err });
        formError.value = err instanceof Error ? err.message : 'Failed to save entry';
      } finally {
        formSubmitting.value = false;
      }
    };
    
    const confirmDelete = (entry: KnowledgebaseEntry) => {
      entryToDelete.value = entry;
      showDeleteModal.value = true;
    };
    
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      entryToDelete.value = null;
    };
    
    const deleteEntry = async () => {
      if (!entryToDelete.value || !entryToDelete.value.id) return;
      
      deleteSubmitting.value = true;
      
      try {
        await knowledgebaseService.deleteEntry(entryToDelete.value.id);
        logger.info('Deleted knowledgebase entry', LogCategory.DATABASE, { 
          id: entryToDelete.value.id,
          topic: entryToDelete.value.topic
        });
        
        // Refresh entries
        await fetchEntries();
        
        // Close modal
        closeDeleteModal();
      } catch (err) {
        logger.error('Error deleting knowledgebase entry', LogCategory.DATABASE, { error: err });
        error.value = err instanceof Error ? err : new Error('Failed to delete entry');
      } finally {
        deleteSubmitting.value = false;
      }
    };
    
    const parseBulletpoints = (description: string): string[] => {
      return knowledgebaseService.parseBulletpoints(description);
    };
    
    // Fetch entries on component mount
    onMounted(() => {
      fetchEntries();
    });
    
    return {
      entries,
      loading,
      error,
      searchTerm,
      filteredEntries,
      showEntryModal,
      currentEntry,
      bulletpoints,
      formError,
      formSubmitting,
      showDeleteModal,
      entryToDelete,
      deleteSubmitting,
      fetchEntries,
      handleSearch,
      openEntryModal,
      closeEntryModal,
      addBulletpoint,
      removeBulletpoint,
      saveEntry,
      confirmDelete,
      closeDeleteModal,
      deleteEntry,
      parseBulletpoints
    };
  }
});
</script>
